import { useRef, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { FilePond, registerPlugin } from 'react-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginImageEdit from 'filepond-plugin-image-edit'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import { calculateMinWidthAndHeight } from 'src/consumer/helpers/utilFunctions'

import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'
import 'filepond-plugin-image-edit/dist/filepond-plugin-image-edit.css'

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginImageEdit,
  FilePondPluginFileValidateType,
  FilePondPluginImageTransform,
  FilePondPluginImageValidateSize,
)

/**
 * Hook for FilePond-based image uploads with Doka editing
 *
 * @param {Object} params
 * @param {number} params.aspectWidth - Width for aspect ratio cropping
 * @param {number} params.aspectHeight - Height for aspect ratio cropping
 * @param {Function} params.onUploadComplete - Callback when upload completes (receives imageId)
 * @param {boolean} params.deferUpload - If true, defers upload until manually triggered (default: false)
 * @param {Function} params.onEditComplete - Callback when editing completes in deferred mode (receives {file, blobUrl})
 *
 * @returns {Object} Hook interface
 *
 * @example
 * // Immediate upload mode (default)
 * const { triggerUpload, isUploading, FilePondComponent } = useFilePondUpload({
 *   aspectWidth: 1200,
 *   aspectHeight: 630,
 *   onUploadComplete: (imageId) => console.log('Uploaded:', imageId)
 * })
 *
 * @example
 * // Deferred upload mode
 * const { triggerUpload, editedFile, localPreviewUrl, clearDeferredFile, FilePondComponent } = useFilePondUpload({
 *   aspectWidth: 1200,
 *   aspectHeight: 630,
 *   deferUpload: true,
 *   onEditComplete: ({ file, blobUrl }) => console.log('Edited:', file, blobUrl)
 * })
 *
 * // Later, upload using useImageUpload hook:
 * const { uploadImage } = useImageUpload()
 * const imageId = await uploadImage(editedFile)
 */
/**
 * Phase 14j — image-compression knobs surface from FilePond's already-registered
 * FilePondPluginImageTransform. With the doka cropping editor producing a sized output, an
 * additional JPEG re-encode at 85% quality typically shrinks profile-pic uploads from
 * 2-5MB camera images down to 60-200KB on the wire without visible quality loss.
 *
 * Callers can override per-field:
 *   - resizeTargetWidth / resizeTargetHeight → final pixel dimensions (default: aspectWidth/aspectHeight)
 *   - outputQuality                          → JPEG quality 1-100 (default: 85)
 *   - outputMimeType                         → 'image/jpeg' to force a single format and drop alpha (default)
 */
const DEFAULT_OUTPUT_QUALITY = 85
const DEFAULT_OUTPUT_MIME_TYPE = 'image/jpeg'

export function useFilePondUpload({
  aspectWidth,
  aspectHeight,
  onUploadComplete,
  deferUpload = false,
  onEditComplete,
  resizeTargetWidth,
  resizeTargetHeight,
  outputQuality = DEFAULT_OUTPUT_QUALITY,
  outputMimeType = DEFAULT_OUTPUT_MIME_TYPE,
}) {
  const filePondRef = useRef(null)
  const [isUploading, setIsUploading] = useState(false)
  const [editedFile, setEditedFile] = useState(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null)
  const [dokaEditor, setDokaEditor] = useState(null)
  const dokaEditorRef = useRef(null)
  const dokaLoadingPromiseRef = useRef(null)
  const isMountedRef = useRef(true)

  const onUploadCompleteRef = useRef(onUploadComplete)
  const onEditCompleteRef = useRef(onEditComplete)

  onUploadCompleteRef.current = onUploadComplete
  onEditCompleteRef.current = onEditComplete

  const cssAspectRatio = aspectWidth && aspectHeight ? aspectWidth / aspectHeight : undefined
  const minDimensions = cssAspectRatio ? calculateMinWidthAndHeight(cssAspectRatio) : null

  async function ensureDokaEditor() {
    if (dokaEditorRef.current) {
      return dokaEditorRef.current
    }

    if (!dokaLoadingPromiseRef.current) {
      dokaLoadingPromiseRef.current = import('src/consumer/helpers/dokaLoader')
        .then(({ createDokaEditor }) =>
          createDokaEditor({
            cropAspectRatio: cssAspectRatio ? 1 / cssAspectRatio : undefined,
          }),
        )
        .then((editor) => {
          if (!isMountedRef.current) {
            try {
              editor.destroy?.()
            } catch (_error) {
              // Editor may already be destroyed during teardown.
            }
            return null
          }
          dokaEditorRef.current = editor
          setDokaEditor(editor)
          return editor
        })
        .finally(() => {
          dokaLoadingPromiseRef.current = null
        })
    }

    return dokaLoadingPromiseRef.current
  }

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
      if (dokaEditorRef.current) {
        try {
          dokaEditorRef.current.destroy?.()
        } catch (_error) {
          // Editor may already be destroyed during teardown.
        }
        dokaEditorRef.current = null
      }
    }
  }, [localPreviewUrl])

  const processImage = async (fieldName, file, metadata, load, error) => {
    if (deferUpload) {
      // Deferred upload mode: Store file locally without uploading
      try {
        const blobUrl = URL.createObjectURL(file)
        setEditedFile(file)
        setLocalPreviewUrl(blobUrl)

        // Notify parent that editing is complete
        onEditCompleteRef.current?.({ file, blobUrl })

        // Mark as "processed" with a temporary ID
        load(`temp-${Date.now()}`)
      } catch (err) {
        toast.error('Failed to process image')
        error('Processing failed')
      }
    } else {
      // Immediate upload mode: Upload to server right away.
      //
      // Bug fix: capture a local blob preview URL of the (compressed) file BEFORE the upload
      // completes. After upload, we keep showing the blob until the consuming component
      // re-mounts. The media CDN can take a few seconds to propagate a freshly-uploaded asset
      // to the edge — without this, the parent <img src={mediaURL+id}> would 404, render a
      // BrokenImage placeholder, and only "fix itself" on a reupload (when the CDN has caught
      // up). With the blob preview the just-uploaded image shows immediately.
      let blobUrl = null
      try {
        blobUrl = URL.createObjectURL(file)
        setLocalPreviewUrl(blobUrl)
      } catch (_e) {
        // Object URL creation can fail in obscure browsers — degrade gracefully to the CDN path.
      }
      try {
        setIsUploading(true)
        const formData = new FormData()
        formData.append('file', file)

        const response = await postDataWithoutParams('/api/media/file/upload', formData)

        if (response?.status === 200) {
          const imageId = response.data.fileId
          onUploadCompleteRef.current?.(imageId, { blobUrl, file })
          load(imageId)
        } else {
          toast.error('Upload failed')
          error('Upload failed')
        }
      } catch (err) {
        toast.error('Something went wrong')
        error('Upload error')
      } finally {
        setIsUploading(false)
      }
    }
  }

  const clearDeferredFile = () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl)
    }
    setEditedFile(null)
    setLocalPreviewUrl(null)
  }

  const triggerUpload = async () => {
    try {
      await ensureDokaEditor()
      window.requestAnimationFrame(() => {
        filePondRef.current?.browse()
      })
    } catch (error) {
      console.error('Failed to load Doka editor:', error)
      toast.error('Image editor failed to load. Please try again.')
    }
  }

  const FilePondComponent = () => (
    <div style={{ display: 'none' }}>
      <FilePond
        ref={filePondRef}
        allowImageEdit
        allowImageValidateSize
        imageValidateSizeMinWidth={minDimensions?.minWidth}
        imageValidateSizeMinHeight={minDimensions?.minHeight}
        imagePreviewHeight={aspectHeight}
        instantUpload={true}
        allowMultiple={false}
        server={{
          process: processImage,
        }}
        acceptedFileTypes={['image/*']}
        name="files"
        imageEditInstantEdit
        allowImageTransform
        // Phase 14j — JPEG re-encode + downscale so a 5MB camera shot doesn't go to the wire.
        // Doka has already cropped to aspect; this is the "compress to a sane delivery size" step.
        imageResizeTargetWidth={resizeTargetWidth || aspectWidth}
        imageResizeTargetHeight={resizeTargetHeight || aspectHeight}
        imageResizeMode="cover"
        imageTransformOutputMimeType={outputMimeType}
        imageTransformOutputQuality={outputQuality}
        styleItemPanelAspectRatio={cssAspectRatio ? 1 / cssAspectRatio : null}
        stylePanelAspectRatio={cssAspectRatio ? 1 / cssAspectRatio : null}
        imageEditEditor={dokaEditor}
      />
    </div>
  )

  return {
    triggerUpload,
    isUploading,
    FilePondComponent,
    // Deferred upload mode values
    editedFile,
    localPreviewUrl,
    clearDeferredFile,
  }
}
